import React, { useState, useEffect, useCallback } from 'react';
import StandardModal from './StandardModal';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  AlertCircle, Package, Users, Clock, User, Phone, Mail, Plus, X, 
  MapPin, Calendar, DollarSign, Gift, FileText, CreditCard, RefreshCw,
  Check, Loader2, Info
} from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './Buttons';
import TimeSlotSelector from './TimeSlotSelector';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Comprehensive Edit Booking Modal
 * - All booking fields editable
 * - Availability check for date/time changes
 * - Add-on management with automatic Stripe charges/refunds
 * - Price tracking and display
 */
const EditBookingModal = ({
  isOpen,
  onClose,
  trip,
  assets,
  teamMembers,
  roles,
  allBookings,
  onSave,
  timeFormat,
  services = [],
  addOns = []
}) => {
  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    duration: 60,
    assetId: '',
    teamAssignments: [],
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    partySize: 1,
    pickupLocation: '',
    emergencyContactNumber: '',
    notes: '',
    selectedAddOns: [],
    sendFollowUpEmail: true,
    includeReviewLink: true
  });
  
  const [originalData, setOriginalData] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [serviceAddOns, setServiceAddOns] = useState([]);
  const [priceBreakdown, setPriceBreakdown] = useState({
    original: 0,
    current: 0,
    difference: 0
  });
  const [saving, setSaving] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Initialize form data when trip changes
  useEffect(() => {
    if (trip) {
      const initialData = {
        date: trip.date || '',
        startTime: trip.startTime || '',
        duration: trip.duration || 60,
        assetId: trip.assetId || '',
        teamAssignments: trip.teamAssignments || [],
        customerName: trip.customerName || '',
        customerPhone: trip.customerPhone || '',
        customerEmail: trip.customerEmail || '',
        partySize: trip.partySize || 1,
        pickupLocation: trip.pickupLocation || trip.locationDetails || '',
        emergencyContactNumber: trip.emergencyContactNumber || '',
        notes: trip.notes || '',
        selectedAddOns: trip.selectedAddOns || [],
        sendFollowUpEmail: trip.sendFollowUpEmail !== false, // Default to true
        includeReviewLink: trip.includeReviewLink !== false  // Default to true
      };
      setFormData(initialData);
      setOriginalData(initialData);
      
      // Calculate original price
      const originalTotal = trip.paidAmount || trip.totalAmount || trip.servicePrice || 0;
      setPriceBreakdown({
        original: originalTotal,
        current: originalTotal,
        difference: 0
      });
      
      // Load service add-ons if we have the service
      if (trip.serviceId && services.length > 0) {
        const service = services.find(s => s.id === trip.serviceId);
        if (service?.addOns) {
          setServiceAddOns(service.addOns);
        }
      }
    }
  }, [trip, services]);

  // Calculate price when add-ons change
  useEffect(() => {
    if (!originalData) return;
    
    const originalAddOnsTotal = (originalData.selectedAddOns || []).reduce(
      (sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0
    );
    
    const currentAddOnsTotal = (formData.selectedAddOns || []).reduce(
      (sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0
    );
    
    const basePrice = (priceBreakdown.original || 0) - originalAddOnsTotal;
    const newTotal = basePrice + currentAddOnsTotal;
    const difference = currentAddOnsTotal - originalAddOnsTotal;
    
    setPriceBreakdown(prev => ({
      ...prev,
      current: newTotal,
      difference: difference
    }));
  }, [formData.selectedAddOns, originalData, priceBreakdown.original]);

  // Helper: Convert time to minutes
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper: Check time overlap
  const checkTimeOverlap = (trip1Start, trip1Duration, trip2Start, trip2Duration) => {
    const start1 = timeToMinutes(trip1Start);
    const end1 = start1 + trip1Duration;
    const start2 = timeToMinutes(trip2Start);
    const end2 = start2 + trip2Duration;
    return start1 < end2 && end1 > start2;
  };

  // Helper: Calculate end time
  const calculateEndTime = (startTime, duration) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + duration;
    const hours = Math.floor(endMinutes / 60) % 24;
    const mins = endMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Check for conflicts whenever form changes
  useEffect(() => {
    if (!formData.startTime || !formData.date) return;

    const detectedConflicts = [];
    const availableSuggestions = [];

    // Check asset conflicts
    if (formData.assetId && formData.assetId !== 'unassigned') {
      const assetConflicts = allBookings.filter(t =>
        t.id !== trip?.id &&
        t.date === formData.date &&
        t.assetId === formData.assetId &&
        checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
      );

      if (assetConflicts.length > 0) {
        const asset = assets.find(b => b.id === formData.assetId);
        detectedConflicts.push({
          type: 'asset',
          message: `${asset?.name} is booked ${assetConflicts[0].startTime} - ${calculateEndTime(assetConflicts[0].startTime, assetConflicts[0].duration)}`
        });

        // Suggest available assets
        const availableAssets = assets.filter(b =>
          b.active &&
          !allBookings.some(t =>
            t.id !== trip?.id &&
            t.date === formData.date &&
            t.assetId === b.id &&
            checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
          )
        );

        if (availableAssets.length > 0) {
          availableSuggestions.push({
            type: 'asset',
            items: availableAssets.slice(0, 3).map(v => ({ id: v.id, name: v.name }))
          });
        }
      }
    }

    // Check team conflicts
    formData.teamAssignments.forEach((assignment) => {
      if (assignment.teamId) {
        const teamConflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.teamAssignments?.some(a => a.teamId === assignment.teamId) &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );

        if (teamConflicts.length > 0) {
          const team = teamMembers.find(c => c.id === assignment.teamId);
          detectedConflicts.push({
            type: 'team',
            message: `${team?.name} is booked ${teamConflicts[0].startTime} - ${calculateEndTime(teamConflicts[0].startTime, teamConflicts[0].duration)}`
          });
        }
      }
    });

    setConflicts(detectedConflicts);
    setSuggestions(availableSuggestions);
  }, [formData.startTime, formData.date, formData.assetId, formData.teamAssignments, formData.duration, allBookings, trip, assets, teamMembers]);

  // Handle asset change
  const handleAssetChange = (assetId) => {
    setFormData(prev => ({ ...prev, assetId: assetId }));

    if (assetId !== 'unassigned' && assetId) {
      const selectedAsset = assets.find(b => b.id === assetId);
      if (selectedAsset?.teamRequirements) {
        const autoTeamSlots = [];
        selectedAsset.teamRequirements.forEach(req => {
          for (let i = 0; i < req.quantity; i++) {
            autoTeamSlots.push({
              teamId: '',
              teamName: '',
              roleId: req.roleId,
              roleName: req.roleName
            });
          }
        });
        setFormData(prev => ({ ...prev, teamAssignments: autoTeamSlots }));
      }
    }
  };

  // Handle add-on toggle
  const handleAddOnToggle = (addon, checked) => {
    setFormData(prev => {
      if (checked) {
        // Add the add-on
        return {
          ...prev,
          selectedAddOns: [...prev.selectedAddOns, { ...addon, quantity: 1 }]
        };
      } else {
        // Remove the add-on
        return {
          ...prev,
          selectedAddOns: prev.selectedAddOns.filter(a => a.id !== addon.id && a.name !== addon.name)
        };
      }
    });
  };

  // Handle add-on quantity change
  const handleAddOnQuantityChange = (addonId, quantity) => {
    setFormData(prev => ({
      ...prev,
      selectedAddOns: prev.selectedAddOns.map(addon =>
        (addon.id === addonId || addon.name === addonId) 
          ? { ...addon, quantity: Math.max(1, quantity) }
          : addon
      )
    }));
  };

  // Check if add-on is selected
  const isAddOnSelected = (addon) => {
    return formData.selectedAddOns.some(a => a.id === addon.id || a.name === addon.name);
  };

  // Get add-on quantity
  const getAddOnQuantity = (addon) => {
    const selected = formData.selectedAddOns.find(a => a.id === addon.id || a.name === addon.name);
    return selected?.quantity || 1;
  };

  // Handle save with Stripe charges/refunds
  const handleSave = async () => {
    if (conflicts.length > 0) {
      const confirmSave = window.confirm(
        `Warning: ${conflicts.length} conflict(s) detected:\n${conflicts.map(c => c.message).join('\n')}\n\nDo you want to save anyway?`
      );
      if (!confirmSave) return;
    }

    setSaving(true);
    
    try {
      // Check if there's a price difference
      const priceDiff = priceBreakdown.difference;
      
      if (Math.abs(priceDiff) > 0.01) {
        if (priceDiff > 0) {
          // Need to charge more - create additional payment
          try {
            const chargeResponse = await api.post(`/bookings/${trip.id}/add-charge`, {
              amount: priceDiff,
              description: 'Additional add-ons',
              addOns: formData.selectedAddOns
            });
            
            if (chargeResponse.data.checkoutUrl) {
              // Redirect to Stripe checkout for additional payment
              toast.info('Redirecting to payment for additional charges...');
              // Open in same window for PWA/app compatibility
              window.location.href = chargeResponse.data.checkoutUrl;
              return;
            } else {
              toast.success(`Additional charge of $${priceDiff.toFixed(2)} processed`);
            }
          } catch (chargeError) {
            console.error('Charge error:', chargeError);
            toast.error('Could not process additional charge. Saving booking without payment update.');
          }
        } else if (priceDiff < 0) {
          // Need to refund - issue partial refund
          try {
            await api.post(`/bookings/${trip.id}/partial-refund`, {
              amount: Math.abs(priceDiff),
              reason: 'Add-ons removed from booking'
            });
            toast.success(`Refund of $${Math.abs(priceDiff).toFixed(2)} issued`);
          } catch (refundError) {
            console.error('Refund error:', refundError);
            toast.error('Could not process refund. Please issue manually.');
          }
        }
      }
      
      // Save the booking data
      await onSave(trip.id, {
        ...formData,
        // Include price info
        totalAmount: priceBreakdown.current,
        priceModified: Math.abs(priceDiff) > 0.01,
        priceModificationNote: priceDiff > 0 
          ? `Additional charge: $${priceDiff.toFixed(2)}`
          : priceDiff < 0 
            ? `Refund issued: $${Math.abs(priceDiff).toFixed(2)}`
            : null
      });
      
      onClose();
      toast.success('Booking updated successfully!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save booking');
    } finally {
      setSaving(false);
    }
  };

  // Get assets with availability status
  const getAssetsWithStatus = () => {
    return assets.map(asset => {
      let available = true;
      let reason = '';

      if (!asset.active) {
        available = false;
        reason = 'Inactive';
      }

      if (available && formData.startTime && formData.date) {
        const conflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.assetId === asset.id &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );
        if (conflicts.length > 0) {
          available = false;
          reason = `Booked ${conflicts[0].startTime}`;
        }
      }

      return { ...asset, available, reason };
    });
  };

  // Get team with availability status
  const getTeamWithStatus = (roleId) => {
    return teamMembers.map(c => {
      let available = true;
      let reason = '';

      if (roleId && c.roleId !== roleId) {
        available = false;
        const teamRole = roles.find(r => r.id === c.roleId);
        reason = teamRole?.name || 'Different role';
      }

      if (available && formData.startTime && formData.date) {
        const conflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.teamAssignments?.some(a => a.teamId === c.id) &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );
        if (conflicts.length > 0) {
          available = false;
          reason = `Booked ${conflicts[0].startTime}`;
        }
      }

      return { ...c, available, reason };
    });
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  if (!trip) return null;

  const assetsWithStatus = getAssetsWithStatus();
  const availableAddOns = serviceAddOns.length > 0 ? serviceAddOns : addOns;

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <span>Edit Booking</span>
          <Badge variant="outline" className="font-mono">{trip.confirmationCode}</Badge>
        </div>
      }
      description={trip.serviceName || 'Update booking details'}
      size="lg"
      footer={
        <div className="flex items-center justify-between w-full">
          {/* Price Summary */}
          <div className="text-sm">
            {priceBreakdown.difference !== 0 && (
              <span className={priceBreakdown.difference > 0 ? 'text-orange-600 font-medium' : 'text-green-600 font-medium'}>
                {priceBreakdown.difference > 0 ? '+' : ''}${priceBreakdown.difference.toFixed(2)}
                {priceBreakdown.difference > 0 ? ' (charge customer)' : ' (refund to customer)'}
              </span>
            )}
          </div>
          
          {/* Customer notification notice */}
          {trip?.customerEmail && (
            <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
              <Mail className="w-3.5 h-3.5" />
              <span>Customer will be emailed about date, time, or location changes</span>
            </div>
          )}
          
          <div className="flex gap-2">
            <SecondaryButton onClick={onClose} disabled={saving}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </PrimaryButton>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Conflicts Warning */}
        {conflicts.length > 0 && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-800 mb-1">Conflicts Detected</p>
                {conflicts.map((conflict, idx) => (
                  <p key={idx} className="text-xs text-orange-700">• {conflict.message}</p>
                ))}
              </div>
            </div>
            {suggestions.length > 0 && suggestions[0].type === 'asset' && (
              <div className="mt-2 pt-2 border-t border-orange-200">
                <p className="text-xs font-semibold text-orange-800 mb-1">Available:</p>
                <div className="flex flex-wrap gap-1">
                  {suggestions[0].items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleAssetChange(item.id)}
                      className="px-2 py-1 bg-white border border-orange-300 rounded text-xs text-orange-700 hover:bg-orange-100"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Date, Time & Duration */}
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4" />
            Date & Time
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Start Time</Label>
              <TimeSlotSelector
                value={formData.startTime}
                onChange={(time) => setFormData(prev => ({ ...prev, startTime: time }))}
              />
            </div>
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Duration</Label>
              <Select
                value={String(formData.duration)}
                onValueChange={(val) => setFormData(prev => ({ ...prev, duration: parseInt(val) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[30, 60, 90, 120, 150, 180, 240, 300, 360, 480].map(mins => (
                    <SelectItem key={mins} value={String(mins)}>
                      {mins >= 60 ? `${mins / 60}h` : `${mins}m`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
            <User className="w-4 h-4" />
            Customer
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Name</Label>
              <Input
                value={formData.customerName}
                onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Phone</Label>
              <Input
                value={formData.customerPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Email</Label>
              <Input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Party Size</Label>
              <Input
                type="number"
                min={1}
                value={formData.partySize}
                onChange={(e) => setFormData(prev => ({ ...prev, partySize: parseInt(e.target.value) || 1 }))}
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4" />
            Location
          </h3>
          <Input
            value={formData.pickupLocation}
            onChange={(e) => setFormData(prev => ({ ...prev, pickupLocation: e.target.value }))}
            placeholder="Pickup location or meeting point"
          />
        </div>

        {/* Emergency Contact */}
        <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
          <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 text-orange-600" />
            Emergency Contact
          </h3>
          <Input
            value={formData.emergencyContactNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactNumber: e.target.value }))}
            placeholder="+1 (555) 123-4567"
          />
          <p className="text-xs text-gray-500 mt-1">Shown on customer ticket for emergencies</p>
        </div>

        {/* Asset & Team Assignment */}
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
            <Package className="w-4 h-4" />
            Assignment
          </h3>
          <div className="space-y-3">
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Asset</Label>
              <Select value={formData.assetId || 'unassigned'} onValueChange={handleAssetChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select asset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {assetsWithStatus.map(asset => (
                    <SelectItem 
                      key={asset.id} 
                      value={asset.id}
                      disabled={!asset.available}
                    >
                      {asset.name} {!asset.available && `(${asset.reason})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Team Assignments */}
            {formData.teamAssignments.length > 0 && (
              <div>
                <Label className="mb-2 block text-xs text-gray-500">Team</Label>
                <div className="space-y-2">
                  {formData.teamAssignments.map((assignment, idx) => {
                    const teamWithStatus = getTeamWithStatus(assignment.roleId);
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-20">{assignment.roleName || 'Team'}:</span>
                        <Select
                          value={assignment.teamId || 'unassigned'}
                          onValueChange={(val) => {
                            const newAssignments = [...formData.teamAssignments];
                            const selectedTeam = teamMembers.find(c => c.id === val);
                            newAssignments[idx] = {
                              ...newAssignments[idx],
                              teamId: val === 'unassigned' ? '' : val,
                              teamName: selectedTeam?.name || ''
                            };
                            setFormData(prev => ({ ...prev, teamAssignments: newAssignments }));
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select team member" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {teamWithStatus.map(team => (
                              <SelectItem 
                                key={team.id} 
                                value={team.id}
                                disabled={!team.available}
                              >
                                {team.name} {!team.available && `(${team.reason})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add-ons */}
        {availableAddOns.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
              <Gift className="w-4 h-4" />
              Add-ons
            </h3>
            <div className="space-y-2">
              {availableAddOns.map((addon, idx) => (
                <div 
                  key={addon.id || idx} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isAddOnSelected(addon) ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isAddOnSelected(addon)}
                      onCheckedChange={(checked) => handleAddOnToggle(addon, checked)}
                    />
                    <div>
                      <p className="font-medium text-gray-900">{addon.name}</p>
                      <p className="text-sm text-gray-500">${(addon.price || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  {isAddOnSelected(addon) && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-500">Qty:</Label>
                      <Input
                        type="number"
                        min={1}
                        value={getAddOnQuantity(addon)}
                        onChange={(e) => handleAddOnQuantityChange(addon.id || addon.name, parseInt(e.target.value) || 1)}
                        className="w-16 h-8 text-center"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Currently selected add-ons from booking that aren't in available list */}
            {formData.selectedAddOns.filter(
              selected => !availableAddOns.some(a => a.id === selected.id || a.name === selected.name)
            ).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Previously added:</p>
                {formData.selectedAddOns.filter(
                  selected => !availableAddOns.some(a => a.id === selected.id || a.name === selected.name)
                ).map((addon, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-2 bg-gray-100 rounded mb-1"
                  >
                    <span className="text-sm">{addon.name} - ${(addon.price || 0).toFixed(2)}</span>
                    <button
                      onClick={() => handleAddOnToggle(addon, false)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4" />
            Notes
          </h3>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Internal notes about this booking..."
            rows={3}
          />
        </div>

        {/* Follow-Up Email Settings */}
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2 text-sm">
            <Mail className="w-4 h-4 text-amber-600" />
            Follow-Up Email Settings
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="sendFollowUpEmail"
                checked={formData.sendFollowUpEmail}
                onCheckedChange={(checked) => setFormData(prev => ({ 
                  ...prev, 
                  sendFollowUpEmail: checked,
                  // If disabling follow-up, also disable review link
                  includeReviewLink: checked ? prev.includeReviewLink : false
                }))}
              />
              <div className="flex-1">
                <Label htmlFor="sendFollowUpEmail" className="text-sm font-medium cursor-pointer">
                  Send follow-up thank you email
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Automatically send a thank you email after the trip ends
                </p>
              </div>
            </div>
            
            {formData.sendFollowUpEmail && (
              <div className="flex items-start gap-3 ml-6 pt-2 border-t border-amber-200">
                <Checkbox
                  id="includeReviewLink"
                  checked={formData.includeReviewLink}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, includeReviewLink: checked }))}
                />
                <div className="flex-1">
                  <Label htmlFor="includeReviewLink" className="text-sm font-medium cursor-pointer text-blue-600">
                    Include review link
                  </Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Add a link for customers to leave a review
                  </p>
                </div>
              </div>
            )}
            
            {!formData.sendFollowUpEmail && (
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-100 rounded p-2">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>No email will be sent to this customer after their trip</span>
              </div>
            )}
          </div>
        </div>

        {/* Price Summary */}
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4" />
            Price Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Original Total:</span>
              <span className="font-medium">${priceBreakdown.original.toFixed(2)}</span>
            </div>
            {priceBreakdown.difference !== 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Add-on Changes:</span>
                  <span className={priceBreakdown.difference > 0 ? 'text-orange-600 font-medium' : 'text-green-600 font-medium'}>
                    {priceBreakdown.difference > 0 ? '+' : ''}${priceBreakdown.difference.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-blue-200">
                  <span className="font-semibold text-gray-900">New Total:</span>
                  <span className="font-bold text-gray-900">${priceBreakdown.current.toFixed(2)}</span>
                </div>
                <div className="mt-2 p-2 bg-white rounded border border-blue-200">
                  {priceBreakdown.difference > 0 ? (
                    <p className="text-xs text-orange-700 flex items-center gap-1">
                      <CreditCard className="w-3 h-3" />
                      Customer will be charged an additional ${priceBreakdown.difference.toFixed(2)}
                    </p>
                  ) : (
                    <p className="text-xs text-green-700 flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" />
                      Customer will be refunded ${Math.abs(priceBreakdown.difference).toFixed(2)}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </StandardModal>
  );
};

export default EditBookingModal;
